/**
 * Formulari FIR — elenco da RENTRI + validatore FIR live (DM 59/2023, Reg. CE 1013/2006).
 *
 * Il validatore consente di provare le regole di business su un FIR senza
 * inviarlo a RENTRI: utile per chi sta preparando una bozza in /drafts oppure
 * per controllare manualmente un FIR prima di chiedere la vidimazione.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  FileSignature,
  FileText,
  PenLine,
  Plus,
  Radio,
  Settings2,
  ShieldCheck,
  Sparkles,
  XCircle,
} from "lucide-react";
import { FirDettaglioDialog } from "@/components/FirDettaglioDialog";
import { FirTrasmissioneDettaglioDialog } from "@/components/FirTrasmissioneDettaglioDialog";
import { FirStatoBadge } from "@/components/FirStatoBadge";
import { NuovoFormularioDialog } from "@/components/NuovoFormularioDialog";
import { XfirEnableWizard } from "@/components/XfirEnableWizard";
import { isXfirEnabled } from "@/lib/xfirEnabled";
import { FirPinSignDialog } from "@/components/FirPinSignDialog";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { getUserPreferences } from "@/components/PreferenzeOperative";
import { cn } from "@/lib/utils";
import { endpoints } from "@/api/client";
import type { ValidationIssue } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState, EmptyState } from "@/components/ui/feedback";
import {
  firListRowFields,
  firNeedsSign,
  firTrasmissioneRowFields,
  type FirRecord,
} from "@/lib/fir";
import { ChoiceSelect, ChoiceCombobox, ChoiceMultiSelect } from "@/components/forms";
import {
  UNITA_MISURA_FORM,
  normalizeCodificaItem,
  useRentriChoices,
  type RentriChoice,
} from "@/lib/rentriChoices";

const FIR_PRESETS: { label: string; payload: Record<string, unknown> }[] = [
  {
    label: "FIR completo non pericoloso (valido)",
    payload: {
      data_emissione: new Date().toISOString().slice(0, 10),
      produttore: {
        denominazione: "Acme Costruzioni Srl",
        num_iscr: "10018310010",
        codice_fiscale: "12345678901",
      },
      destinatario: {
        denominazione: "EcoSmaltimenti SpA",
        num_iscr: "10020450067",
      },
      trasportatore: {
        denominazione: "TrasportoVerde Srl",
        num_iscr: "10020450099",
      },
      rifiuto: {
        eer: "170504",
        quantita: 1500,
        unita_misura: "KG",
      },
    },
  },
  {
    label: "FIR rifiuto pericoloso senza HP/ADR (warning)",
    payload: {
      data_emissione: new Date().toISOString().slice(0, 10),
      produttore: { denominazione: "Officina Centrale", num_iscr: "10018310010" },
      destinatario: { denominazione: "EcoOlii Srl" },
      trasportatore: { denominazione: "ADR Trasporti" },
      rifiuto: { eer: "130208*", quantita: 200, unita_misura: "KG" },
    },
  },
  {
    label: "FIR pericoloso completo HP+ADR (valido)",
    payload: {
      data_emissione: new Date().toISOString().slice(0, 10),
      produttore: { denominazione: "Industria Chimica", num_iscr: "10018310010" },
      destinatario: { denominazione: "Smaltimento Speciale Srl" },
      trasportatore: { denominazione: "ADR Logistica Spa" },
      rifiuto: {
        eer: "170410*",
        quantita: 800,
        unita_misura: "KG",
        caratteristiche_pericolo: ["HP14", "HP7"],
      },
      adr: { un: "UN3077", classe: "9", gruppo_imballaggio: "III" },
    },
  },
  {
    label: "Trasportatore mancante (errore)",
    payload: {
      data_emissione: new Date().toISOString().slice(0, 10),
      produttore: { denominazione: "Acme" },
      destinatario: { denominazione: "Eco Srl" },
      rifiuto: { eer: "170504", quantita: 100, unita_misura: "KG" },
    },
  },
];

export function Formulari() {
  const ctx = useOperatoreContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const cf = ctx.cf;
  const sito = ctx.numIscrSito;
  const [firDettaglio, setFirDettaglio] = useState<string | null>(null);
  const [txDettaglio, setTxDettaglio] = useState<FirRecord | null>(null);
  const [firTab, setFirTab] = useState<"digitali" | "cartacei">(
    () => getUserPreferences().formulari_tab_default ?? "digitali",
  );
  const [xfirWizardOpen, setXfirWizardOpen] = useState(false);
  const [nuovoOpen, setNuovoOpen] = useState(false);
  const [filtroNumero, setFiltroNumero] = useState("");
  const [xfirEnabled, setXfirEnabled] = useState(() => (sito ? isXfirEnabled(sito) : false));
  const [pinFir, setPinFir] = useState<string | null>(null);
  const [validatorOpen, setValidatorOpen] = useState(false);

  useEffect(() => {
    if (sito) setXfirEnabled(isXfirEnabled(sito));
  }, [sito]);

  useEffect(() => {
    if (searchParams.get("nuovo") === "1") {
      setNuovoOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, []);

  const q = useQuery({
    queryKey: ["formulari", cf, sito],
    queryFn: () =>
      endpoints.formulari.lista({
        identificativo_soggetto: cf || undefined,
        num_iscr_sito: sito || undefined,
      }),
    enabled: !!(cf || sito),
  });
  const listRaw = (Array.isArray(q.data) ? q.data : []) as FirRecord[];
  const filtro = filtroNumero.trim().toLowerCase();
  const list = filtro
    ? listRaw.filter((f) => firListRowFields(f).numero.toLowerCase().includes(filtro))
    : listRaw;

  const txQ = useQuery({
    queryKey: ["fir-trasmissioni", sito],
    queryFn: () => endpoints.formulari.trasmissioni(sito!),
    enabled: !!sito,
  });
  const txList = (Array.isArray(txQ.data) ? txQ.data : []) as FirRecord[];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <FileText className="h-7 w-7" /> Formulari FIR
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Formulari digitali e cartacei — Reg. CE 1013/2006, DM 59/2023. Usa CF e unità locale
            nella barra laterale.
            {sito && (
              <>
                {" "}
                Unità: <span className="font-mono text-foreground">{sito}</span>
                {ctx.sitoLabel ? ` — ${ctx.sitoLabel}` : ""}
              </>
            )}
          </p>
        </div>
        {firTab === "digitali" && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!sito}
              onClick={() => setXfirWizardOpen(true)}
            >
              <Settings2 className="mr-1.5 h-4 w-4" />
              {xfirEnabled ? "XFIR collegato" : "Abilita XFIR"}
            </Button>
            <Button size="sm" disabled={!sito} onClick={() => setNuovoOpen(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Nuovo formulario
            </Button>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {firTab === "digitali" ? "Formulari digitali" : "Formulari cartacei"}
            </CardTitle>
            <div className="flex rounded-md border p-0.5">
              <button
                type="button"
                className={cn(
                  "rounded px-3 py-1 text-xs font-medium",
                  firTab === "digitali" && "bg-primary text-primary-foreground",
                )}
                onClick={() => setFirTab("digitali")}
              >
                Digitali
              </button>
              <button
                type="button"
                className={cn(
                  "rounded px-3 py-1 text-xs font-medium",
                  firTab === "cartacei" && "bg-primary text-primary-foreground",
                )}
                onClick={() => setFirTab("cartacei")}
              >
                Cartacei
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!cf && !sito && (
            <p className="text-sm text-amber-700 dark:text-amber-400 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3">
              Imposta CF e unità locale nella barra laterale per caricare i formulari.
            </p>
          )}
          {firTab === "digitali" && sito && !xfirEnabled && (
            <p className="text-sm text-amber-700 dark:text-amber-400 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 p-3">
              Per creare e firmare formulari digitali collega questa unità locale a XFIR RENTRI (
              <button
                type="button"
                className="underline font-medium"
                onClick={() => setXfirWizardOpen(true)}
              >
                Abilita XFIR
              </button>
              ). In demo: codice <span className="font-mono">MOCKXFIR_DEMO</span>.
            </p>
          )}
          {firTab === "digitali" && listRaw.length > 0 && (
            <div className="max-w-xs">
              <label className="text-xs font-medium text-muted-foreground">Filtra per numero FIR</label>
              <Input
                className="mt-1"
                placeholder="Es. IT000…"
                value={filtroNumero}
                onChange={(e) => setFiltroNumero(e.target.value)}
              />
            </div>
          )}
          {q.isLoading && <CenteredLoader />}
          {q.error && <ErrorState error={q.error} />}
          {firTab === "cartacei" && (
            <div className="space-y-4">
              <EmptyState
                title="Formulari cartacei"
                description="Vidimazione virtuale, stampa e verifica numero FIR — dalla sezione dedicata."
              />
              <Button asChild variant="default">
                <Link to="/vidimazione">
                  <FileSignature className="mr-2 h-4 w-4" /> Vai a Vidimazione FIR
                </Link>
              </Button>
            </div>
          )}
          {firTab === "digitali" && !q.isLoading && !q.error && listRaw.length === 0 && (cf || sito) && (
            <div className="space-y-3">
              <EmptyState
                title="Nessun formulario digitale"
                description="Crea una bozza con «Nuovo formulario», completa in Bozze e invia a RENTRI dopo XFIR."
              />
              <div className="flex justify-center">
                <Button size="sm" disabled={!sito} onClick={() => setNuovoOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" /> Nuovo formulario
                </Button>
              </div>
            </div>
          )}
          {firTab === "digitali" && !q.isLoading && listRaw.length > 0 && list.length === 0 && (
            <p className="text-sm text-muted-foreground">Nessun FIR corrisponde al filtro.</p>
          )}
          {firTab === "digitali" && list.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>Numero FIR</TH>
                    <TH>Data emissione</TH>
                    <TH>Codice EER</TH>
                    <TH className="text-right">Quantità</TH>
                    <TH>Stato</TH>
                    <TH>Produttore</TH>
                    <TH>Destinatario</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {list.slice(0, 200).map((f) => {
                    const row = firListRowFields(f);
                    return (
                      <TR
                        key={row.numero}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setFirDettaglio(row.numero)}
                      >
                        <TD className="font-mono text-xs whitespace-nowrap">{row.numero}</TD>
                        <TD className="text-xs whitespace-nowrap">{row.dataEmissione}</TD>
                        <TD className="font-mono text-xs">{row.eer}</TD>
                        <TD className="text-right text-sm tabular-nums whitespace-nowrap">
                          {row.quantita}
                        </TD>
                        <TD>
                          <FirStatoBadge fir={f} />
                        </TD>
                        <TD className="max-w-[160px] truncate text-sm" title={row.produttore}>
                          {row.produttore}
                        </TD>
                        <TD className="max-w-[160px] truncate text-sm" title={row.destinatario}>
                          {row.destinatario}
                        </TD>
                        <TD className="text-right whitespace-nowrap">
                          {firTab === "digitali" &&
                            xfirEnabled &&
                            firNeedsSign(row.statoRaw) && (
                              <Button
                                size="sm"
                                variant="default"
                                className="mr-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setPinFir(row.numero);
                                }}
                              >
                                <PenLine className="mr-1 h-3.5 w-3.5" /> Firma
                              </Button>
                            )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFirDettaglio(row.numero);
                            }}
                          >
                            Dettaglio
                          </Button>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Radio className="h-5 w-5" /> Trasmissioni dati FIR (consultazione RENTRI)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Elenco delle trasmissioni verso RENTRI per l&apos;unità locale indicata sopra. Solo i FIR con
            rifiuti pericolosi possono essere trasmessi.
          </p>
          {!sito && (
            <p className="text-xs text-amber-600">Seleziona l&apos;unità locale nella barra laterale.</p>
          )}
          {txQ.isLoading && <CenteredLoader />}
          {txQ.error && <ErrorState error={txQ.error} />}
          {!txQ.isLoading && !txQ.error && sito && txList.length === 0 && (
            <EmptyState
              title="Nessuna trasmissione"
              description="Crea un FIR pericoloso, completa il trasporto e trasmetti i dati"
            />
          )}
          {txList.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <THead>
                  <TR>
                    <TH>Identificativo</TH>
                    <TH>Numero FIR</TH>
                    <TH>Data trasmissione</TH>
                    <TH>Data emissione</TH>
                    <TH>Codice EER</TH>
                    <TH className="text-right">Quantità</TH>
                    <TH>Utente</TH>
                    <TH>Tipo</TH>
                    <TH>Stato</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {txList.map((t) => {
                    const row = firTrasmissioneRowFields(t);
                    return (
                      <TR
                        key={row.identificativo}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setTxDettaglio(t)}
                      >
                        <TD className="font-mono text-xs">{row.identificativo}</TD>
                        <TD className="font-mono text-xs whitespace-nowrap">{row.numeroFir}</TD>
                        <TD className="text-xs whitespace-nowrap">{row.dataTrasmissione}</TD>
                        <TD className="text-xs whitespace-nowrap">{row.dataEmissione}</TD>
                        <TD className="font-mono text-xs">{row.eer}</TD>
                        <TD className="text-right text-sm tabular-nums">{row.quantita}</TD>
                        <TD className="font-mono text-xs">{row.utente}</TD>
                        <TD>
                          <Badge variant="secondary">{row.tipo}</Badge>
                        </TD>
                        <TD>
                          <Badge variant={row.stato === "Attivo" ? "success" : "outline"}>
                            {row.stato}
                          </Badge>
                        </TD>
                        <TD className="text-right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              setTxDettaglio(t);
                            }}
                          >
                            Dettaglio
                          </Button>
                        </TD>
                      </TR>
                    );
                  })}
                </TBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <FirDettaglioDialog
        numeroFir={firDettaglio}
        open={!!firDettaglio}
        onOpenChange={(o) => !o && setFirDettaglio(null)}
        onFirma={
          xfirEnabled
            ? (nf) => {
                setFirDettaglio(null);
                setPinFir(nf);
              }
            : undefined
        }
      />
      <FirTrasmissioneDettaglioDialog
        trasmissione={txDettaglio}
        open={!!txDettaglio}
        onOpenChange={(o) => !o && setTxDettaglio(null)}
        onApriFir={(nf) => {
          setTxDettaglio(null);
          setFirDettaglio(nf);
        }}
      />
      {sito && (
        <XfirEnableWizard
          open={xfirWizardOpen}
          onOpenChange={setXfirWizardOpen}
          numIscrSito={sito}
          onEnabled={() => setXfirEnabled(true)}
        />
      )}
      <NuovoFormularioDialog open={nuovoOpen} onOpenChange={setNuovoOpen} />
      <FirPinSignDialog
        open={!!pinFir}
        onOpenChange={(o) => !o && setPinFir(null)}
        firNumero={pinFir ?? ""}
        numIscrSito={sito}
        onSigned={() => q.refetch()}
      />
      <Card>
        <button
          type="button"
          className="flex w-full items-center justify-between px-6 py-4 text-left"
          onClick={() => setValidatorOpen((v) => !v)}
        >
          <span className="text-base font-medium flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" /> Validatore FIR (anteprima regole)
          </span>
          <ChevronDown
            className={cn("h-5 w-5 transition", validatorOpen && "rotate-180")}
          />
        </button>
        {validatorOpen && (
          <CardContent className="border-t pt-0">
            <FirValidator />
          </CardContent>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validatore FIR live
// ---------------------------------------------------------------------------

function FirValidator() {
  const [dataEmissione, setDataEmissione] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [prodNome, setProdNome] = useState("Acme Costruzioni Srl");
  const [prodIscr, setProdIscr] = useState("10018310010");
  const [destNome, setDestNome] = useState("EcoSmaltimenti SpA");
  const [trasNome, setTrasNome] = useState("TrasportoVerde Srl");
  const [eer, setEer] = useState("170504");
  const [qta, setQta] = useState("1500");
  const [um, setUm] = useState("KG");
  const [hp, setHp] = useState<string[]>([]);
  const [adrUn, setAdrUn] = useState("");

  const hpQ = useRentriChoices("caratteristiche-pericolo");
  const hpOptions = hpQ.data ?? [];
  const eerQ = useQuery({
    queryKey: ["codifiche-eer-choices"],
    queryFn: async (): Promise<RentriChoice[]> => {
      const rows = await endpoints.codifiche.lookup("codici-eer");
      const list = Array.isArray(rows) ? rows : [];
      return list
        .map((r) => {
          const c = normalizeCodificaItem(r);
          if (!c) return null;
          return { ...c, value: c.value.replace(/\./g, "") };
        })
        .filter((x): x is RentriChoice => !!x);
    },
    staleTime: 24 * 60 * 60 * 1000,
  });
  const eerOptions = eerQ.data ?? [];

  const mut = useMutation({
    mutationFn: (payload: Record<string, unknown>) => endpoints.validate.fir(payload),
  });

  const buildPayload = (): Record<string, unknown> => {
    const rifiuto: Record<string, unknown> = {
      eer,
      quantita: qta === "" ? null : Number(qta),
      unita_misura: um,
    };
    if (hp.length > 0) {
      rifiuto.caratteristiche_pericolo = hp;
    }
    const payload: Record<string, unknown> = {
      data_emissione: dataEmissione,
      produttore: prodNome
        ? { denominazione: prodNome, num_iscr: prodIscr || undefined }
        : undefined,
      destinatario: destNome ? { denominazione: destNome } : undefined,
      trasportatore: trasNome ? { denominazione: trasNome } : undefined,
      rifiuto,
    };
    if (adrUn.trim()) payload.adr = { un: adrUn.trim() };
    return payload;
  };

  const submit = () => mut.mutate(buildPayload());

  const applyPreset = (p: (typeof FIR_PRESETS)[number]) => {
    const x = p.payload;
    const rifiuto = (x.rifiuto as Record<string, unknown>) ?? {};
    const prod = (x.produttore as Record<string, unknown>) ?? {};
    const dest = (x.destinatario as Record<string, unknown>) ?? {};
    const tras = (x.trasportatore as Record<string, unknown> | undefined) ?? {};
    const adr = (x.adr as Record<string, unknown> | undefined) ?? {};

    setDataEmissione(String(x.data_emissione ?? dataEmissione));
    setProdNome(String(prod.denominazione ?? ""));
    setProdIscr(String(prod.num_iscr ?? ""));
    setDestNome(String(dest.denominazione ?? ""));
    setTrasNome(String(tras.denominazione ?? ""));
    setEer(String(rifiuto.eer ?? ""));
    setQta(String(rifiuto.quantita ?? ""));
    setUm(String(rifiuto.unita_misura ?? "KG"));
    setHp(
      Array.isArray(rifiuto.caratteristiche_pericolo)
        ? (rifiuto.caratteristiche_pericolo as string[])
        : []
    );
    setAdrUn(String(adr.un ?? ""));
    setTimeout(() => mut.mutate(x), 50);
  };

  const result = mut.data;

  return (
    <div className="space-y-4 pt-4">
      <p className="text-xs text-muted-foreground">
        Reg. CE 1013/2006 — controlla le regole prima di inviare a RENTRI (strumento avanzato).
      </p>
        <div className="flex flex-wrap gap-2">
          {FIR_PRESETS.map((p) => (
            <Button
              key={p.label}
              type="button"
              size="sm"
              variant="outline"
              onClick={() => applyPreset(p)}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {p.label}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Field label="Data emissione">
            <Input
              type="date"
              value={dataEmissione}
              onChange={(e) => setDataEmissione(e.target.value)}
            />
          </Field>
          <Field label="Produttore — denominazione">
            <Input value={prodNome} onChange={(e) => setProdNome(e.target.value)} />
          </Field>
          <Field label="Produttore — n. iscrizione">
            <Input
              value={prodIscr}
              onChange={(e) => setProdIscr(e.target.value)}
              className="font-mono"
            />
          </Field>
          <Field label="Destinatario — denominazione">
            <Input value={destNome} onChange={(e) => setDestNome(e.target.value)} />
          </Field>
          <Field label="Trasportatore — denominazione">
            <Input value={trasNome} onChange={(e) => setTrasNome(e.target.value)} />
          </Field>
          <Field label="ADR — UN (opz.)">
            <Input
              value={adrUn}
              onChange={(e) => setAdrUn(e.target.value)}
              placeholder="UN3077"
              className="font-mono"
            />
          </Field>
          <Field label="EER rifiuto">
            <ChoiceCombobox
              value={eer}
              onChange={(v) => setEer(v.replace(/\./g, ""))}
              options={eerOptions}
              loading={eerQ.isLoading}
              formatValue={(v) => {
                const clean = v.replace(/\./g, "");
                return clean.length === 6
                  ? `${clean.slice(0, 2)}.${clean.slice(2, 4)}.${clean.slice(4)}`
                  : clean;
              }}
            />
          </Field>
          <Field label="Quantità">
            <Input
              type="number"
              value={qta}
              onChange={(e) => setQta(e.target.value)}
            />
          </Field>
          <Field label="Unità di misura">
            <ChoiceSelect
              value={um}
              onChange={(v) => setUm(v || "KG")}
              options={UNITA_MISURA_FORM}
              required
            />
          </Field>
          <Field label="Caratteristiche di pericolo (HP)">
            <ChoiceMultiSelect
              values={hp}
              onChange={setHp}
              options={hpOptions}
              loading={hpQ.isLoading}
              emptyText="Nessuna caratteristica HP disponibile"
            />
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={submit} disabled={mut.isPending}>
            {mut.isPending ? "Validazione…" : "Valida FIR"}
          </Button>
          {mut.error && <ErrorState error={mut.error} />}
        </div>

        {result && <FirOutcome valid={result.valid} issues={result.issues} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function FirOutcome({
  valid,
  issues,
}: {
  valid: boolean;
  issues: ValidationIssue[];
}) {
  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");

  return (
    <div className="space-y-3">
      <div
        className={`flex items-center gap-3 rounded-md border p-3 ${
          valid
            ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
            : "border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
        }`}
      >
        {valid ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
        <div className="text-sm">
          <span className="font-semibold">
            {valid ? "FIR valido" : "FIR non valido"}
          </span>
          <span className="ml-2 text-xs opacity-80">
            {errors.length} errori · {warnings.length} avvisi
          </span>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="space-y-2">
          {issues.map((it, idx) => (
            <div
              key={idx}
              className={`flex items-start gap-3 rounded-md border p-3 text-sm ${
                it.severity === "error"
                  ? "border-rose-500/30 bg-rose-500/5"
                  : "border-amber-500/30 bg-amber-500/5"
              }`}
            >
              {it.severity === "error" ? (
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />
              ) : (
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
              )}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={it.severity === "error" ? "destructive" : "secondary"}>
                    {it.code}
                  </Badge>
                  <span className="font-mono text-xs text-muted-foreground">{it.field}</span>
                </div>
                <p className="mt-1 text-foreground/90">{it.message}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
